import { useMemo, useRef } from 'react';
import ViewTabsDnD from 'framework-listing-lib/internal/ViewTabs/components/ViewTabsDnD';
import { EMPTY_STATE_TYPE } from 'framework-listing-lib/constants/emptyState';
import useEmptyState from 'framework-listing-lib/hooks/useEmptyState';
import { useHasViews } from 'framework-listing-lib/internal/ViewTabs/hooks/useViewProps';
import { useIsFoldersMode } from 'framework-listing-lib/internal/hooks/useUIMode';
import { useHasFoldersTab } from 'framework-listing-lib/internal/Folders/hooks/useFolderProps';
import useShowLoadingSkeleton from 'framework-listing-lib/internal/hooks/useShowLoadingSkeleton';
import CrmListingFiltersSkeleton from 'framework-listing-lib/internal/FilterBar/components/CrmListingFiltersSkeleton';
import { CrmListingFilterBar } from 'framework-listing-lib/internal/FilterBar/components/CrmListingFilterBar';
import BUDeprecationBanner from 'framework-listing-lib/internal/businessUnits/components/BUDeprecationBanner';
import FilterBarStartSlot from 'framework-listing-lib/internal/FilterBar/components/FilterBarStartSlot';
import { useGlobalError } from 'framework-listing-lib/internal/providers/DataProvider';
import { NETWORK_ERROR_403 } from 'framework-listing-lib/constants/networkErrors';
import { useHasSearchInput } from 'framework-listing-lib/internal/hooks/useSearchInputProps';
import useCrmObjectTypeListingEmptyState from 'framework-listing-lib/internal/hooks/useCrmObjectTypeListingEmptyState';
import CrmListingSaveViewButton from 'framework-listing-lib/internal/ViewTabs/components/CrmListingSaveViewButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function ListingLibFilters({
  filterComponent,
  scrollableQuickFilters = false
}) {
  const [globalError] = useGlobalError();
  const hasViews = useHasViews();
  const hasSearchInput = useHasSearchInput();
  const {
    emptyStateResult
  } = useEmptyState();
  const {
    emptyStateType
  } = useCrmObjectTypeListingEmptyState();
  const isFoldersMode = useIsFoldersMode();
  const hasFoldersTab = useHasFoldersTab();
  const showLoadingSkeleton = useShowLoadingSkeleton();
  /**
   * Track if this component has been already rendered for future loads:
   *  - When loading the UI for the first time
   *    - We don't render ListingLibFilters until we know the status of empty state: this is to prevent showing the filter bar and immediately hiding it after we realize it is the first time a user is visiting the page.
   *  - Future loads (table sorting or filtering) after ListingLibFilters has been already rendered: ok to render the filter bar + view tabs
   */
  const hasAlreadyLoadedRef = useRef(false);
  const isEmptyStateFirstTimeInApp = useMemo(() => Boolean(emptyStateType === EMPTY_STATE_TYPE.FIRST_TIME_IN_APP), [emptyStateType]);
  const isEmptyStatePendingButHasNeverRendered = useMemo(() => Boolean(emptyStateResult && emptyStateResult.isPending && hasAlreadyLoadedRef.current === false), [emptyStateResult]);
  if (showLoadingSkeleton) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [hasViews && /*#__PURE__*/_jsx(ViewTabsDnD, {}), /*#__PURE__*/_jsx(CrmListingFiltersSkeleton, {})]
    });
  }

  /**
   * Don't render UIFilterBar if in initial state mode or if none of the components have been defined
   * so it doesn't take up extra space on screen.
   */
  if (!emptyStateResult || isEmptyStatePendingButHasNeverRendered || isEmptyStateFirstTimeInApp || !filterComponent && !hasSearchInput && !hasViews) {
    return null;
  }
  hasAlreadyLoadedRef.current = true;
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [hasViews && /*#__PURE__*/_jsx(ViewTabsDnD, {}), /*#__PURE__*/_jsx(CrmListingFilterBar, {
      hasViews: hasViews,
      isFoldersMode: isFoldersMode,
      isFoldersTabActive: hasFoldersTab && isFoldersMode,
      "data-test-id": "crm-listing-filter-bar",
      endSlot: globalError.statusCode === NETWORK_ERROR_403 ? null : !isFoldersMode && hasViews && /*#__PURE__*/_jsx(CrmListingSaveViewButton, {}),
      startSlot: globalError.statusCode === NETWORK_ERROR_403 ? null : /*#__PURE__*/_jsx(FilterBarStartSlot, {
        filterComponent: filterComponent
      }),
      scrollable: Boolean(scrollableQuickFilters)
    }), /*#__PURE__*/_jsx(BUDeprecationBanner, {})]
  });
}